import React, { useState } from 'react';
import type { UserFilterOptions, UserStatus, UserRole } from '@/types';

interface UserFiltersProps {
  onFiltersChange: (filters: UserFilterOptions) => void;
  onClearFilters: () => void;
}

const USER_STATUSES: { value: UserStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'locked', label: 'Locked' },
];

const USER_ROLES: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'mod', label: 'Moderator' },
  { value: 'user', label: 'User' },
  { value: 'readonly', label: 'Read Only' },
];

export function UserFilters({ onFiltersChange, onClearFilters }: UserFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<UserFilterOptions>({});

  const handleFilterChange = (key: keyof UserFilterOptions, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleStatusChange = (value: string) => {
    if (value === '') {
      handleFilterChange('status', undefined);
    } else {
      handleFilterChange('status', value as UserStatus);
    }
  };

  const handleRoleChange = (value: string) => {
    if (value === '') {
      handleFilterChange('role', undefined);
    } else {
      handleFilterChange('role', value as UserRole);
    }
  };

  const handleApplyFilters = () => {
    onFiltersChange(filters);
  };

  const handleClearFilters = () => {
    setFilters({});
    onClearFilters();
  };

  const hasActiveFilters = Object.values(filters).some(value => {
    return value !== undefined && value !== null && value !== '';
  });

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.email) count++;
    if (filters.status) count++;
    if (filters.role) count++;
    return count;
  };

  return (
    <>
      <div className="filter-card">
        <div 
          className={`filter-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="filter-header-content">
            <h3 className="filter-title">Filter Users</h3>
            <p className="filter-description">Search and filter user data</p>
          </div>
          <button className="access-btn access-ghost">
            <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
            <span className="text-caption">{isExpanded ? '▲' : '▼'}</span>
          </button>
        </div>

        <div className={`filter-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="filter-content">
            <div className="access-form">
              <div className="access-form-item">
                <label className="access-label" htmlFor="userEmail">
                  Email
                </label>
                <input
                  id="userEmail"
                  type="text"
                  placeholder="Enter email..."
                  value={filters.email || ''}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  className="access-input"
                />
              </div>

              <div className="access-form-item">
                <label className="access-label" htmlFor="userStatus">
                  Status
                </label>
                <select
                  id="userStatus"
                  value={filters.status || ''}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="access-select"
                >
                  <option value="">All Statuses</option>
                  {USER_STATUSES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="access-form-item">
                <label className="access-label" htmlFor="userRole">
                  Role
                </label>
                <select
                  id="userRole"
                  value={filters.role || ''}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="access-select"
                >
                  <option value="">All Roles</option>
                  {USER_ROLES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="filter-actions">
              <button
                onClick={handleClearFilters}
                className="access-btn access-secondary"
              >
                Clear All Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="access-btn access-primary"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="alert alert-info">
          <span>
            {getActiveFiltersCount()} filter{getActiveFiltersCount() > 1 ? 's' : ''} applied
          </span>
          <button onClick={handleClearFilters}>
            Clear all filters
          </button>
        </div>
      )}
    </>
  );
}
